import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  serviceFleetLatestGetLatest,
  serviceVehicleGetList,
  serviceFleetLatestUpdateStatusOffline,
  serviceCheckTrial,
  serviceAdminUsage
} from "../../api/serviceFleetMaster";
import {
  isSameFleetRecord,
  normaliseFleetList,
  normaliseDetailsList,
  computeHasAlert,
  processBearing
} from "../../utils/fleetUtils";

const clearFlags = (flags = {}) =>
  Object.fromEntries(
    Object.keys(flags).map((key) => [key, false]),
  );

const clearWeights = (weights = {}) =>
  Object.fromEntries(
    Object.entries(weights).map(([key, value]) => [
      key,
      typeof value === "number" ? 0 : value,
    ]),
  );

// Survives component remounts, but resets after a full page refresh.
const adminUsageSent = new Set();

export default function useFleetTracking(adminId, interval = 5000) {
  const OFFLINE_AFTER_MS = 300_000; //5 min in milliseconds, test 10 sec
  const offlineSentRef = useRef(new Set());

  //for caching
  const previousRef = useRef({});

  // internal clock
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  // 0a. Set admin usage once per page load
  useEffect(() => {
    if (!adminId || adminUsageSent.has(adminId)) {
      return;
    }

    adminUsageSent.add(adminId);

    serviceAdminUsage().catch((error) => {
      // Allow another attempt if the request failed.
      adminUsageSent.delete(adminId);
      console.error("Failed to update admin usage:", error);
    });
  }, [adminId]);

  // 0. Check trial before load
  const {
    mutate: checkTrial,
    isPending: isTrialCheckPending,
    isSuccess: isTrialCheckSuccess,
    isError: isTrialCheckError,
    error: trialCheckError,
  } = useMutation({
    mutationKey: ["vehicle-trial-check", adminId],
    mutationFn: () => serviceCheckTrial(),
    retry: 1
  });

  useEffect(() => {
    if (!adminId) return;

    checkTrial();
  }, [adminId, checkTrial]);

  // 1. Fleet polling
  const fleetQuery = useQuery({
    queryKey: ["fleetlatest-list", adminId],
    queryFn: () => serviceFleetLatestGetLatest(),
    refetchInterval: interval,
    refetchIntervalInBackground: true,
    //keepPreviousData: true, // 🔥 prevents UI flicker
    placeholderData: (prev) => prev, // Newer TanStack pattern for keepPreviousData
    staleTime: interval - 1000,
    enabled: !!adminId && isTrialCheckSuccess,
  });

  // 2. vehicle + employee (once)
  const detailsQuery = useQuery({
    queryKey: ["details-list", adminId],
    queryFn: () => serviceVehicleGetList(),

    staleTime: 1_800_000, //30 min, test 10 sec
    refetchInterval: 1_800_000, //30 min, test 10 sec
    refetchIntervalInBackground: true,

    enabled: !!adminId && isTrialCheckSuccess,
  });

  // 3. normalise details
  const detailsMap = useMemo(() => {
    if (!isTrialCheckSuccess) {
      return {};
    }
    return normaliseDetailsList(detailsQuery.data || []);
  }, [detailsQuery.data, isTrialCheckSuccess]);

  // 4. normalise fleet
  const fleetMap = useMemo(() => normaliseFleetList(fleetQuery.data || []), [fleetQuery.data]);

  //const pollingDataUpdatedAt = fleetQuery.dataUpdatedAt;
  // 5. merge layer for easy access
  // returns array for fleet,vehicle,employee merged objects
  const { mergedList, mergedMap } = useMemo(() => {

    const newCache = {}; // important for cleanup
    const list = [];
    const map = {};

    for (const vehicleId in fleetMap) {
      const fleet = fleetMap[vehicleId];
      const details = detailsMap[vehicleId] || {};

      const prev = previousRef.current[vehicleId]; // 👈 cache

      const subscriptionStatus = details?.subscriptionStatus || "INACTIVE";

      const isInactive =
        subscriptionStatus !== "ACTIVE" && subscriptionStatus !== "TRIAL";

      // const lat = fleet.location?.coordinates?.[1] ?? null;
      // const lng = fleet.location?.coordinates?.[0] ?? null;

      const incomingLat =
        fleet.location?.coordinates?.[1] ?? null;

      const incomingLng =
        fleet.location?.coordinates?.[0] ?? null;

      const trackingBearing = isInactive
        ? null
        : processBearing({
          previousRecord: prev,
          currentRecord: fleet,
          lat: incomingLat,
          lng: incomingLng,
        });

      const isStale = now - fleet.ts > OFFLINE_AFTER_MS;
      const effectiveStatus = isStale ? "offline" : fleet.status;

      const speed = Number(fleet.speedKmh) || 0;

      // 1. create next object for check
      const next = {
        vehicleId,
        //LIVE
        // ...fleet,

        ...(isInactive
          ? {
            address: "",

            flags: clearFlags(fleet.flags),

            location: null,
            lat: null,
            lng: null,
            trackingBearing: null,

            speedKmh: 0,

            weights: clearWeights(fleet.weights),
            totalWeight: 0,
            truckWeight: 0,
            trailerWeight: 0,

            source: fleet.source,
          }
          : {
            ...fleet,

            lat: incomingLat,
            lng: incomingLng,
            trackingBearing,
          }),

        //STATIC details
        vehicleCode: details?.vehicleCode,
        imageUrl: details?.imageUrl,
        weightsMax: details?.weightsMax,
        totalWeightsMax: details?.totalWeightsMax,
        fuel: details?.fuel,
        voltage: details?.voltage,
        employee: details?.employee,
        subscriptionStatus,

        status: effectiveStatus,

        //flags
        isOverweight:
          fleet.totalWeight != null && details?.totalWeightsMax != null
            ? fleet.totalWeight > details.totalWeightsMax
            : false,

        isStale,
        isMoving: !isStale && speed > 0,

        hasAlert: !isInactive && computeHasAlert(fleet, details)
      };

      // 👇 shallow compare → reuse object if no change
      // 2. compare with previous
      const final = prev && isSameFleetRecord(prev, next) ? prev : next;

      // 3. store new object
      newCache[vehicleId] = final;
      map[vehicleId] = final;
      list.push(final);
    }

    // 4. replace cache completely
    previousRef.current = newCache;

    return { mergedList: list, mergedMap: map };
  }, [fleetMap, detailsMap, now]);

  // for selected fleet handle
  // const mergedMap = useMemo(() => {
  //   const map = {};
  //   mergedList.forEach((f) => {
  //     map[f.vehicleId] = f;
  //   });

  //   return map;
  // }, [mergedList]);

  //for stats
  const stats = useMemo(() => {
    let online = 0;
    let offline = 0;
    let notMoving = 0;

    let totalSpeed = 0;

    mergedList.forEach((f) => {
      if (f.status === "online") online++;
      if (f.status === "offline") offline++;
      if (f.speedKmh === 0) notMoving++;

      totalSpeed += f.speedKmh || 0;
    });

    return {
      total: mergedList.length,
      online,
      offline,
      notMoving,
      avgSpeed: mergedList.length ? Math.round(totalSpeed / mergedList.length) : 0,
    };
  }, [mergedList]);


  useEffect(() => {
    mergedList.forEach((fleet) => {
      if (!fleet.isStale) {
        offlineSentRef.current.delete(fleet.vehicleId);
        return;
      }

      if (fleet.status === 'offline') {
        //if already offline then no needed
        return;
      }

      if (offlineSentRef.current.has(fleet.vehicleId)) {
        return;
      }

      offlineSentRef.current.add(fleet.vehicleId);

      //todo ujj offline should be server managed. future scope
      serviceFleetLatestUpdateStatusOffline({
        vehicleId: fleet.vehicleId
      }).catch(() => {
        offlineSentRef.current.delete(fleet.vehicleId);
      });
    })
  }, [mergedList]);

  const isLoading =
    isTrialCheckPending ||
    fleetQuery.isLoading ||
    detailsQuery.isLoading;

  const isError =
    isTrialCheckError ||
    fleetQuery.isError ||
    detailsQuery.isError;

  const error =
    trialCheckError ||
    fleetQuery.error ||
    detailsQuery.error;

  return {
    fleetList: mergedList || [],
    fleetMap,
    detailsMap,
    mergedMap,
    stats,
    isLoading,
    isError,
    // error
  };
}